import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import KofiModal from "../components/KofiModal";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  ArrowLeft, Send, Image, Clock, Shield, Star, Check, X, Users,
  UserPlus, AlertCircle, MessageSquare, Loader2, ChevronRight, Lock, Unlock, DollarSign,
  Trophy, Plus, Trash2, Coins, Package, Search, Skull, Pencil, Flag, Heart
} from "lucide-react";
import ExpeditionPoster from "../components/ExpeditionPoster";
import UserProfileModal from "../components/UserProfileModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXPEDITION_BOSS_IMAGES = {
  "Capt. Latanica": "https://legends.ml/static/images/lib/monster/9420513.png",
  "Chao": "https://iili.io/Bn9xTsn.png",
  "Ephenia": "https://royals-library.netlify.app/images/monsters/5250007.png",
  "Papulatus": "https://legends.ml/static/images/lib/monster/8500001.png",
  "Balrog": "https://royals-library.netlify.app/images/monsters/8830000.png",
  "Crimsonwood Keep Expedition (CWKPQ)": "https://iili.io/BnHY0tj.png",
  "Scarlion": "https://legends.ml/static/images/lib/monster/9420549.png",
  "Targa": "https://legends.ml/static/images/lib/monster/9420544.png",
  "Zakum": "https://legends.ml/static/images/lib/monster/8800000.png",
  "Krexel": "https://iili.io/Bn9cSUP.png",
  "Wu-Ling Yaoseng (Shaolin)": "https://legends.ml/static/images/lib/monster/9600025.png",
  "Horntail": "https://legends.ml/static/images/lib/monster/8810018.png",
  "The Boss Expedition": "https://legends.ml/static/images/lib/monster/9400300.png",
  "Castellan Toad": "https://legends.ml/static/images/lib/monster/9400409.png",
  "Vergamot": "https://royals-library.netlify.app/images/monsters/9400263.png",
  "Dunas v1": "https://royals-library.netlify.app/images/monsters/9400270.png",
  "Nibergen": "https://royals-library.netlify.app/images/monsters/9400271.png",
  "Nameless Magic Monster": "https://royals-library.netlify.app/images/monsters/9400266.png",
  "Dunas v2": "https://royals-library.netlify.app/images/monsters/9400270.png",
  "Auf Haven Expedition": "https://royals-library.netlify.app/images/monsters/9400289.png",
  "King Castle Golem": "https://royals-library.netlify.app/images/monsters/8211004.png",
  "Von Leon": "https://royals-library.netlify.app/images/monsters/8840000.png"
};
const getExpBossImage = (name) => EXPEDITION_BOSS_IMAGES[name] || "https://legends.ml/static/images/lib/monster/6130101.png";

// Format mesos: input is in millions, display as "X,000,000 mesos"
const formatMesos = (value) => {
  if (!value && value !== 0) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  const full = num * 1000000;
  return `${full.toLocaleString()} mesos`;
};

const ExpeditionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [expedition, setExpedition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [availableJobs, setAvailableJobs] = useState({});
  const [userCharacters, setUserCharacters] = useState([]);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [applyForm, setApplyForm] = useState({ selected_characters: [], message: "" });
  const [applying, setApplying] = useState(false);
  const [inviteSearch, setInviteSearch] = useState("");
  const [inviteResults, setInviteResults] = useState([]);
  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [lfgHunters, setLfgHunters] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reporting, setReporting] = useState(false);
  const [showKofiModal, setShowKofiModal] = useState(false);
  
  // Expedition completion state
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeForm, setCompleteForm] = useState({ buyer_alive: true, keep_for_splits: false });
  const [completing, setCompleting] = useState(false);
  
  // Splits management state
  const [newDropName, setNewDropName] = useState("");
  const [newDropValue, setNewDropValue] = useState("");
  const [addingDrop, setAddingDrop] = useState(false);
  
  // Edit expedition state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Accept invitation state
  const [showAcceptCharSelect, setShowAcceptCharSelect] = useState(false);
  const [acceptChars, setAcceptChars] = useState([]);
  
  // Buyer queue state
  const [buyerQueue, setBuyerQueue] = useState([]);
  
  // User profile modal state
  const [profileUserId, setProfileUserId] = useState(null);
  
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastMsgTimestamp = useRef(null);
  const pollRef = useRef(null);

  const fetchExpedition = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_URL}/api/expeditions/${id}`, { headers });
      setExpedition(res.data);
    } catch {
      toast.error("Expedition not found");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  const fetchMessages = useCallback(async (initial = false) => {
    try {
      const params = {};
      if (!initial && lastMsgTimestamp.current) {
        params.since = lastMsgTimestamp.current;
      }
      const res = await axios.get(`${API_URL}/api/chat/expedition/${id}/messages`, { params });
      const msgs = res.data.messages || [];
      if (msgs.length > 0) {
        lastMsgTimestamp.current = msgs[msgs.length - 1].created_at;
        if (initial) {
          setMessages(msgs);
        } else {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newMsgs = msgs.filter(m => !existingIds.has(m.id));
            return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev;
          });
        }
      }
    } catch {
      // Silent fail for polling
    }
  }, [id]);

  useEffect(() => {
    fetchExpedition();
    fetchMessages(true);
    axios.get(`${API_URL}/api/jobs`).then(r => setAvailableJobs(r.data.jobs || {})).catch(() => {});
    // Fetch user characters if logged in (for apply flow)
    if (token) {
      axios.get(`${API_URL}/api/user/characters`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setUserCharacters(r.data.characters || []))
        .catch(() => {});
    }
    // Fetch LFG hunters (for leader view)
    axios.get(`${API_URL}/api/expeditions/lfg`).then(r => setLfgHunters(r.data.lfg_posts || [])).catch(() => {});
    pollRef.current = setInterval(() => {
      fetchMessages(false);
      fetchExpedition(); // Also refresh expedition data for member updates
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [fetchExpedition, fetchMessages, token]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendingRef = useRef(false);
  
  const handleSendMessage = async (screenshotUrl = null) => {
    const text = newMessage.trim();
    if (!text && !screenshotUrl) return;
    if (sendingRef.current) return; // Prevent double-send
    sendingRef.current = true;
    setSendingMessage(true);
    const savedMsg = newMessage;
    setNewMessage(""); // Clear immediately
    try {
      await axios.post(`${API_URL}/api/chat/expedition/${id}/messages`, {
        message: text || (screenshotUrl ? "Screenshot" : ""),
        screenshot_url: screenshotUrl
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchMessages(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
      setNewMessage(savedMsg); // Restore on failure
    } finally {
      setSendingMessage(false);
      sendingRef.current = false;
    }
  };

  const handleUploadScreenshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only images allowed"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_URL}/api/upload/screenshot`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      await handleSendMessage(res.data.url);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API_URL}/api/expeditions/${id}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status changed to ${newStatus}`);
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change status");
    }
  };

  const handleAcceptMember = async (memberId) => {
    try {
      await axios.put(`${API_URL}/api/expeditions/${id}/member-action`, { member_id: memberId, action: "accept" }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Member accepted");
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleRejectMember = async (memberId) => {
    try {
      await axios.put(`${API_URL}/api/expeditions/${id}/member-action`, { member_id: memberId, action: "reject" }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Member rejected");
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const toggleCharacterSelection = (char) => {
    const isSelected = applyForm.selected_characters.some(c => c.character_id === char.id);
    if (isSelected) {
      setApplyForm({ ...applyForm, selected_characters: applyForm.selected_characters.filter(c => c.character_id !== char.id) });
    } else {
      setApplyForm({ ...applyForm, selected_characters: [...applyForm.selected_characters, { character_id: char.id, job: char.job, level: char.level, name: char.name }] });
    }
  };

  const handleApplyExpedition = async () => {
    if (!user) { toast.error("Please log in to apply"); return; }
    if (applyForm.selected_characters.length === 0) { toast.error("Please select at least one character"); return; }
    setApplying(true);
    try {
      await axios.post(`${API_URL}/api/expeditions/${id}/apply`, {
        characters: applyForm.selected_characters,
        message: applyForm.message || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Application submitted! The leader will review it.");
      setShowApplyForm(false);
      setApplyForm({ selected_characters: [], message: "" });
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply");
    } finally {
      setApplying(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const getStatusStyle = (status) => {
    const s = {
      ACTIVE: { bg: "bg-green-500/20", text: "text-green-300", border: "border-green-500/40", label: "Recruiting" },
      ONGOING: { bg: "bg-orange-500/20", text: "text-orange-300", border: "border-orange-500/40", label: "In Progress" },
      AWAITING_BUYER_CONFIRM: { bg: "bg-purple-500/20", text: "text-purple-300", border: "border-purple-500/40", label: "Awaiting Buyer Confirm" },
      SETTLING_SPLITS: { bg: "bg-indigo-500/20", text: "text-indigo-300", border: "border-indigo-500/40", label: "Settling Splits" },
      SUCCEEDED: { bg: "bg-yellow-500/20", text: "text-yellow-300", border: "border-yellow-500/40", label: "Completed" },
      CANCELLED: { bg: "bg-red-500/20", text: "text-red-300", border: "border-red-500/40", label: "Cancelled" },
      SETTLED: { bg: "bg-gray-500/20", text: "text-gray-300", border: "border-gray-500/40", label: "Archived" },
    };
    return s[status] || s.ACTIVE;
  };

  const searchUsersForInvite = async (query) => {
    setInviteSearch(query);
    if (query.length < 2) { setInviteResults([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/users/search-invite?q=${encodeURIComponent(query)}`, { headers: { Authorization: `Bearer ${token}` } });
      setInviteResults(res.data.users || []);
    } catch { setInviteResults([]); }
  };

  const handleInviteUser = async (targetUserId, role = "member") => {
    try {
      await axios.post(`${API_URL}/api/expeditions/${id}/invite`, { user_id: targetUserId, role }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Invitation sent!");
      setInviteSearch("");
      setInviteResults([]);
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to invite");
    }
  };

  const handleAcceptInvite = async () => {
    if (userCharacters.length > 0 && acceptChars.length === 0 && !showAcceptCharSelect) {
      setShowAcceptCharSelect(true);
      return;
    }
    try {
      const payload = { member_id: expedition.pending_invite_id };
      if (acceptChars.length > 0) {
        payload.characters = acceptChars.map(c => ({
          character_id: c.id, name: c.name, job: c.job, level: c.level
        }));
      }
      await axios.put(`${API_URL}/api/expeditions/${id}/accept-invite`, 
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Invite accepted! Welcome to the expedition.");
      setShowAcceptCharSelect(false);
      setAcceptChars([]);
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept invite");
    }
  };

  const toggleAcceptChar = (char) => {
    setAcceptChars(prev => 
      prev.some(c => c.id === char.id) 
        ? prev.filter(c => c.id !== char.id)
        : [...prev, char]
    );
  };

  // Mark expedition as success
  const handleMarkSuccess = async () => {
    setCompleting(true);
    try {
      await axios.put(`${API_URL}/api/expeditions/${id}/mark-success`, completeForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Expedition marked as success!");
      setShowCompleteModal(false);
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark as success");
    } finally {
      setCompleting(false);
    }
  };

  // Buyer confirms success
  const handleBuyerConfirmSuccess = async () => {
    try {
      await axios.put(`${API_URL}/api/expeditions/${id}/buyer-confirm-success`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Success confirmed!");
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to confirm");
    }
  };

  // Add a drop
  const handleAddDrop = async () => {
    if (!newDropName.trim() || !newDropValue) return;
    setAddingDrop(true);
    try {
      await axios.post(`${API_URL}/api/expeditions/${id}/drops`, {
        name: newDropName,
        value: parseFloat(newDropValue)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Drop added!");
      setNewDropName("");
      setNewDropValue("");
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add drop");
    } finally {
      setAddingDrop(false);
    }
  };

  // Update drop (value or sold status)
  const handleUpdateDrop = async (dropId, updates) => {
    try {
      await axios.put(`${API_URL}/api/expeditions/${id}/drops/${dropId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update drop");
    }
  };

  // Delete drop
  const handleDeleteDrop = async (dropId) => {
    try {
      await axios.delete(`${API_URL}/api/expeditions/${id}/drops/${dropId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Drop removed");
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete drop");
    }
  };

  // Mark hunter as paid
  const handleMarkPaid = async (userId, paid) => {
    try {
      await axios.put(`${API_URL}/api/expeditions/${id}/hunter-payment/${userId}`, { user_id: userId, paid }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update payment");
    }
  };

  // Settle expedition
  const handleSettle = async () => {
    if (!window.confirm("Settle and archive this expedition? This cannot be undone.")) return;
    try {
      await axios.put(`${API_URL}/api/expeditions/${id}/settle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Expedition settled and archived!");
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to settle");
    }
  };

  // Edit expedition
  const openEditModal = () => {
    setEditForm({
      expected_time: expedition.expected_time ? new Date(expedition.expected_time).toISOString().slice(0, 16) : "",
      expected_runtime: expedition.expected_runtime || "",
      total_slots: expedition.total_slots || 6,
      price_for_client: expedition.price_for_client || "",
      hb_provided: expedition.hb_provided || false,
      hp_gear_provided: expedition.hp_gear_provided || false,
      hp_gear_value: expedition.hp_gear_value || "",
      safe_run_guaranteed: expedition.safe_run_guaranteed || false,
      selling_service: expedition.selling_service !== false,
      apr_required: expedition.apr_required || false,
      apr_amount: expedition.apr_amount || "",
      onyx_apple_required: expedition.onyx_apple_required || false,
      onyx_apple_amount: expedition.onyx_apple_amount || "",
      message: expedition.message || ""
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const updates = {};
      if (editForm.expected_time && editForm.expected_time !== (expedition.expected_time ? new Date(expedition.expected_time).toISOString().slice(0, 16) : "")) {
        if (new Date(editForm.expected_time) <= new Date()) {
          toast.error("Expected time must be in the future");
          setSaving(false);
          return;
        }
        updates.expected_time = new Date(editForm.expected_time).toISOString();
      }
      if (editForm.total_slots !== expedition.total_slots) updates.total_slots = editForm.total_slots;
      if (editForm.price_for_client !== (expedition.price_for_client || "")) updates.price_for_client = editForm.price_for_client;
      if (editForm.hb_provided !== expedition.hb_provided) updates.hb_provided = editForm.hb_provided;
      if (editForm.hp_gear_provided !== (expedition.hp_gear_provided || false)) updates.hp_gear_provided = editForm.hp_gear_provided;
      if (editForm.hp_gear_value !== (expedition.hp_gear_value || "")) updates.hp_gear_value = editForm.hp_gear_value;
      if (editForm.safe_run_guaranteed !== (expedition.safe_run_guaranteed || false)) updates.safe_run_guaranteed = editForm.safe_run_guaranteed;
      if (editForm.selling_service !== (expedition.selling_service !== false)) updates.selling_service = editForm.selling_service;
      if (editForm.expected_runtime !== (expedition.expected_runtime || "")) updates.expected_runtime = editForm.expected_runtime ? parseInt(editForm.expected_runtime) : null;
      if (editForm.apr_required !== (expedition.apr_required || false)) updates.apr_required = editForm.apr_required;
      if (editForm.apr_amount !== (expedition.apr_amount || "")) updates.apr_amount = editForm.apr_amount ? parseInt(editForm.apr_amount) : null;
      if (editForm.onyx_apple_required !== (expedition.onyx_apple_required || false)) updates.onyx_apple_required = editForm.onyx_apple_required;
      if (editForm.onyx_apple_amount !== (expedition.onyx_apple_amount || "")) updates.onyx_apple_amount = editForm.onyx_apple_amount ? parseInt(editForm.onyx_apple_amount) : null;
      if (editForm.message !== (expedition.message || "")) updates.message = editForm.message;

      if (Object.keys(updates).length === 0) {
        toast.info("No changes to save");
        setSaving(false);
        return;
      }

      await axios.put(`${API_URL}/api/expeditions/${id}/edit`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Expedition updated!");
      setShowEditModal(false);
      fetchExpedition();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  if (!expedition) return null;

  // Access control: only participants can view the full page
  // Invited users see a special page with Accept button
  if (!expedition.is_participant && !expedition.is_invited) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center" data-testid="expedition-access-denied">
        <div className="space-y-4 max-w-lg px-4 w-full">
          {/* Expedition Summary */}
          <div className="rounded-xl p-5 text-center" style={{ background: "linear-gradient(180deg, #0d1b4a 0%, #070e28 100%)", border: "1px solid rgba(59,130,246,0.3)" }}>
            <h2 className="text-xl font-bold text-blue-200 mb-1">EXPEDITION: {expedition.boss_name}</h2>
            <p className="text-sm text-blue-300/60 mb-2">Led by <span className="cursor-pointer hover:text-blue-200 hover:underline" onClick={() => setProfileUserId(expedition.leader_id)} data-testid="view-leader-profile">{expedition.leader_username}</span></p>
            <Badge className={`text-xs ${getStatusStyle(expedition.status).bg} ${getStatusStyle(expedition.status).text}`}>
              {getStatusStyle(expedition.status).label}
            </Badge>
            {expedition.price_for_client && (
              <p className="text-yellow-400 font-bold mt-2">{formatMesos(expedition.price_for_client)}</p>
            )}
          </div>

          {/* Apply Section */}
          {user && expedition.status === "ACTIVE" && !showApplyForm && !expedition.has_pending_application && !expedition.is_invited && !expedition.is_participant && (
            <div className="text-center space-y-3">
              <p className="text-blue-300/60 text-sm">You need to be accepted to view full details and chat.</p>
              <Button
                onClick={() => setShowApplyForm(true)}
                className="w-full font-bold text-base py-3"
                style={{ background: "linear-gradient(180deg, #7c4dff 0%, #5c35cc 100%)", border: "2px solid #9c7cff" }}
                data-testid="expedition-apply-btn"
              >
                <UserPlus className="w-5 h-5 mr-2" /> Apply to Join
              </Button>
            </div>
          )}

          {/* Accept Invitation Section */}
          {user && expedition.is_invited && expedition.pending_invite_id && (
            <div className="text-center space-y-3">
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                <p className="text-green-300 font-medium">You've been invited to this expedition!</p>
                <p className="text-green-300/60 text-sm mt-1">Click below to accept and join the party.</p>
              </div>
              <Button
                onClick={handleAcceptInvite}
                className="w-full font-bold text-base py-3"
                style={{ background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)", border: "2px solid #22c55e" }}
                data-testid="expedition-accept-invite-btn"
              >
                <Check className="w-5 h-5 mr-2" /> Accept Invitation
              </Button>
            </div>
          )}

          {/* Apply Form - Character Selection */}
          {user && showApplyForm && (
            <div className="rounded-xl p-4 space-y-4" style={{ background: "linear-gradient(180deg, #0d1b4a 0%, #0a0f2c 100%)", border: "2px solid #3949ab" }}>
              <h3 className="text-blue-300 font-semibold flex items-center gap-2">
                <UserPlus className="w-4 h-4" /> Select Character(s) to Bring
              </h3>

              {userCharacters.length > 0 ? (
                <div className="space-y-2">
                  {userCharacters.map((char) => {
                    const isSelected = applyForm.selected_characters.some(c => c.character_id === char.id);
                    const charSkills = char.skills || availableJobs[char.job]?.skills || [];
                    return (
                      <div
                        key={char.id}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${isSelected ? "ring-2 ring-blue-400" : "hover:bg-blue-500/10"}`}
                        style={{ background: isSelected ? "rgba(59,130,246,0.2)" : "rgba(0,0,0,0.3)", border: `1px solid ${isSelected ? "rgba(59,130,246,0.6)" : "rgba(59,130,246,0.2)"}` }}
                        onClick={() => toggleCharacterSelection(char)}
                        data-testid={`apply-char-${char.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex gap-1">
                            {charSkills.map((skill, si) => (
                              <img key={si} src={skill.icon} alt={skill.name} className="w-6 h-6" title={skill.name} />
                            ))}
                          </div>
                          <div>
                            <p className="text-blue-100 font-medium">{char.name || char.job}</p>
                            <p className="text-xs text-blue-300/70">{char.job} - Lv.{char.level}</p>
                          </div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? "bg-blue-500 border-blue-400" : "border-blue-500/40"}`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-blue-300/60">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No characters registered. Go to Profile &gt; Characters to add one.</p>
                </div>
              )}

              <Textarea
                placeholder="Message to leader (optional)"
                value={applyForm.message}
                onChange={(e) => setApplyForm({ ...applyForm, message: e.target.value })}
                className="bg-black/40 border-blue-500/40 text-white"
                rows={2}
                data-testid="expedition-apply-message"
              />

              {/* Quest Prerequisite Disclaimer */}
              <div className="p-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <p className="text-xs text-yellow-300/90 leading-relaxed">
                  <Shield className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-yellow-400" />
                  Remember that you need to have done all pre-requisite quests for this expedition if any are necessary.
                </p>
              </div>

              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => { setShowApplyForm(false); setApplyForm({ selected_characters: [], message: "" }); }} className="text-gray-300 flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleApplyExpedition}
                  disabled={applyForm.selected_characters.length === 0 || applying}
                  className="flex-1 font-bold"
                  style={{ background: "linear-gradient(180deg, #7c4dff 0%, #5c35cc 100%)", border: "2px solid #9c7cff" }}
                  data-testid="expedition-apply-submit"
                >
                  {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Apply ({applyForm.selected_characters.length})
                </Button>
              </div>
            </div>
          )}

          {/* Already applied */}
          {user && expedition.has_pending_application && (
            <div className="text-center py-3">
              <Badge className="bg-yellow-500/20 text-yellow-300 px-4 py-2">
                <Clock className="w-4 h-4 mr-2 inline" /> Application Pending — Awaiting Leader Review
              </Badge>
            </div>
          )}

          {/* Not logged in */}
          {!user && (
            <p className="text-blue-300/60 text-sm text-center">Log in to apply for this expedition.</p>
          )}

          {/* Non-active expeditions */}
          {expedition.status !== "ACTIVE" && user && (
            <p className="text-blue-300/60 text-sm text-center">This expedition is no longer recruiting.</p>
          )}

          <div className="text-center">
            <Button onClick={() => navigate(-1)} variant="ghost" className="text-blue-300 hover:bg-blue-500/10" data-testid="expedition-denied-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const statusStyle = getStatusStyle(expedition.status);
  const isLeader = expedition.is_leader;
  const allAccepted = expedition.accepted_members || [];
  // Filter out the buyer from accepted members (they have their own section)
  const accepted = allAccepted.filter(m => m.user_id !== expedition.client_id);
  const pending = expedition.pending_members || [];
  const totalFilled = allAccepted.length + 1; // +1 for leader

  // Invited user view — show expedition summary + character selection + Accept/Decline
  if (expedition.is_invited && !expedition.is_participant) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center" data-testid="expedition-invite-view">
        <div className="space-y-4 max-w-lg px-4 w-full">
          <div className="rounded-xl p-5 text-center" style={{ background: "linear-gradient(180deg, #1a0d40 0%, #0a0628 100%)", border: "2px solid rgba(124,77,255,0.5)" }}>
            <UserPlus className="w-10 h-10 mx-auto text-purple-400 mb-3" />
            <h2 className="text-xl font-bold text-purple-200 mb-1">You've Been Invited!</h2>
            <p className="text-lg text-white font-semibold">{expedition.boss_name} Expedition</p>
            <p className="text-sm text-purple-300/60 mb-2">Led by <span className="cursor-pointer hover:text-purple-200 hover:underline" onClick={() => setProfileUserId(expedition.leader_id)}>{expedition.leader_username}</span></p>
            <Badge className={`text-xs ${statusStyle.bg} ${statusStyle.text}`}>{statusStyle.label}</Badge>
            {expedition.price_for_client && (
              <p className="text-yellow-400 font-bold mt-2">{formatMesos(expedition.price_for_client)}</p>
            )}
            {expedition.message && (
              <p className="text-sm text-purple-200/60 mt-2 italic">"{expedition.message}"</p>
            )}
          </div>

          {/* Character Selection */}
          {userCharacters.length > 0 && (
            <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #0d1b4a 0%, #0a0f2c 100%)", border: "1px solid rgba(59,130,246,0.3)" }}>
              <p className="text-blue-300 text-sm font-semibold">Select Character(s) to Bring:</p>
              <div className="space-y-2">
                {userCharacters.map((char) => {
                  const isSelected = acceptChars.some(c => c.id === char.id);
                  const charSkills = char.skills || availableJobs[char.job]?.skills || [];
                  return (
                    <div
                      key={char.id}
                      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${isSelected ? "ring-2 ring-green-400 bg-green-500/10" : "hover:bg-blue-500/10 bg-black/30"}`}
                      onClick={() => toggleAcceptChar(char)}
                      data-testid={`invite-char-${char.id}`}
                    >
                      <div className="flex items-center gap-2">
                        {charSkills.slice(0, 2).map((skill, si) => (
                          <img key={si} src={skill.icon} alt={skill.name} className="w-5 h-5" title={skill.name} />
                        ))}
                        <div>
                          <p className="text-blue-100 text-sm font-medium">{char.name || char.job}</p>
                          <p className="text-xs text-blue-300/70">{char.job} - Lv.{char.level}</p>
                        </div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? "bg-green-500 border-green-400" : "border-blue-500/40"}`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="flex-1 text-gray-300 border border-gray-600"
            >
              Decline
            </Button>
            <Button
              onClick={handleAcceptInvite}
              className="flex-1 font-bold text-base py-3"
              style={{ background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)", border: "2px solid #22c55e" }}
              data-testid="accept-invite-btn"
            >
              <Check className="w-5 h-5 mr-2" /> Accept Invite
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleReport = async () => {
    if (!reportReason.trim()) return;
    setReporting(true);
    try {
      await axios.post(`${API_URL}/api/hc/report`, {
        target_type: "expedition",
        target_id: id,
        reason: reportReason.trim()
      });
      toast.success("Report submitted. Admins will review it.");
      setShowReportModal(false);
      setReportReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit report");
    } finally {
      setReporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="expedition-detail-page">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b" style={{ background: "linear-gradient(180deg, #0d1b4a 0%, #070e28 100%)", borderColor: "rgba(59,130,246,0.3)" }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-blue-300 hover:bg-blue-500/10" data-testid="expedition-detail-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-blue-200">EXPEDITION: {expedition.boss_name}</h1>
            <div className="flex items-center gap-2 text-xs text-blue-300/60">
              <span>Led by <span className="cursor-pointer hover:text-purple-200 hover:underline" onClick={() => setProfileUserId(expedition.leader_id)}>{expedition.leader_username}</span></span>
              <span>|</span>
              <Badge className={`text-xs ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>{statusStyle.label}</Badge>
              <span>|</span>
              <span>{totalFilled}/{expedition.total_slots || "?"} slots</span>
            </div>
          </div>
          {expedition.price_for_client && (
            <div className="text-right">
              <p className="text-yellow-400 font-bold">{formatMesos(expedition.price_for_client) || "Free"}</p>
              <p className="text-xs text-blue-300/50">Buyer Price</p>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Info + Members */}
        <div className="lg:col-span-1 space-y-4">
          {/* Expedition Poster Card */}
          <ExpeditionPoster 
            expedition={expedition} 
            parchmentBg="" 
            isLeader={isLeader}
            isMember={expedition.is_member}
            isParticipant={expedition.is_participant}
            hasPendingInvite={expedition.is_invited}
            onAcceptInvite={expedition.is_invited ? handleAcceptInvite : undefined}
            bossImage={getExpBossImage(expedition.boss_name)}
          />
          
          {/* Accept Invitation Banner (shown when user is invited, even if admin/participant) */}
          {expedition.is_invited && !expedition.is_member && (
            <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #0a2010 0%, #041208 100%)", border: "2px solid rgba(34,197,94,0.5)" }}>
              <h2 className="text-sm font-semibold text-green-300 uppercase tracking-wider flex items-center gap-2">
                <UserPlus className="w-4 h-4" /> You're Invited!
              </h2>
              
              {/* Character Selection */}
              {userCharacters.length > 0 && (
                <div className="space-y-2">
                  <p className="text-green-200/70 text-xs">Select character(s) to bring:</p>
                  {userCharacters.map((char) => {
                    const isSelected = acceptChars.some(c => c.id === char.id);
                    const charSkills = char.skills || availableJobs[char.job]?.skills || [];
                    return (
                      <div
                        key={char.id}
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${isSelected ? "ring-1 ring-green-400 bg-green-500/10" : "hover:bg-green-500/5 bg-black/20"}`}
                        onClick={() => toggleAcceptChar(char)}
                      >
                        <div className="flex items-center gap-2">
                          {charSkills.slice(0, 2).map((skill, si) => (
                            <img key={si} src={skill.icon} alt={skill.name} className="w-4 h-4" />
                          ))}
                          <span className="text-green-100 text-xs">{char.name || char.job} Lv.{char.level}</span>
                        </div>
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isSelected ? "bg-green-500 border-green-400" : "border-green-500/40"}`}>
                          {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <Button
                className="w-full font-bold"
                style={{ background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)", border: "2px solid #22c55e" }}
                onClick={handleAcceptInvite}
                data-testid="detail-accept-invite-btn"
              >
                <Check className="w-4 h-4 mr-2" /> Accept Invitation {acceptChars.length > 0 ? `(${acceptChars.length} chars)` : ""}
              </Button>
            </div>
          )}

          {/* Status Control (Leader Only) */}
          {isLeader && (expedition.status === "ACTIVE" || expedition.status === "ONGOING") && (
            <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #1a1040 0%, #0f0828 100%)", border: "1px solid rgba(124,77,255,0.3)" }}>
              <h2 className="text-sm font-semibold text-purple-300 uppercase tracking-wider flex items-center gap-2">
                <Shield className="w-4 h-4" /> Leader Controls
              </h2>
              <div className="flex flex-wrap gap-2">
                {expedition.status === "ACTIVE" && (
                  <Button size="sm" className="bg-orange-600/30 hover:bg-orange-600/50 text-orange-200 border border-orange-500/40 text-xs" onClick={() => handleStatusChange("ONGOING")} data-testid="expedition-set-ongoing">
                    <Lock className="w-3 h-3 mr-1" /> Start (Lock)
                  </Button>
                )}
                {expedition.status === "ONGOING" && (
                  <>
                    <Button size="sm" className="bg-green-600/30 hover:bg-green-600/50 text-green-200 border border-green-500/40 text-xs" onClick={() => handleStatusChange("ACTIVE")} data-testid="expedition-set-active">
                      <Unlock className="w-3 h-3 mr-1" /> Reopen
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-yellow-600/30 hover:bg-yellow-600/50 text-yellow-200 border border-yellow-500/40 text-xs" 
                      onClick={() => setShowCompleteModal(true)} 
                      data-testid="expedition-mark-success"
                    >
                      <Trophy className="w-3 h-3 mr-1" /> Mark Success
                    </Button>
                  </>
                )}
                <Button size="sm" className="bg-blue-600/20 hover:bg-blue-600/40 text-blue-200 border border-blue-500/40 text-xs" onClick={openEditModal} data-testid="expedition-edit">
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button size="sm" className="bg-red-600/30 hover:bg-red-600/50 text-red-200 border border-red-500/40 text-xs" onClick={() => handleStatusChange("CANCELLED")} data-testid="expedition-cancel">
                  <X className="w-3 h-3 mr-1" /> Cancel
                </Button>
              </div>
              {/* Invite Users */}
              <div className="pt-2 border-t border-purple-500/20">
                <Button
                  size="sm"
                  className="w-full bg-blue-600/30 hover:bg-blue-600/50 text-blue-200 border border-blue-500/40 text-xs"
                  onClick={() => setShowInvitePanel(!showInvitePanel)}
                  data-testid="toggle-invite-panel"
                >
                  <UserPlus className="w-3 h-3 mr-1" /> {showInvitePanel ? "Hide" : "Invite Users"}
                </Button>
                {showInvitePanel && (
                  <div className="mt-2 space-y-2">
                    <Input
                      placeholder="Search username..."
                      value={inviteSearch}
                      onChange={(e) => searchUsersForInvite(e.target.value)}
                      className="bg-black/40 border-blue-500/40 text-white text-xs h-8"
                      data-testid="invite-search-input"
                    />
                    {inviteResults.map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-2 bg-black/30 rounded text-xs">
                        <div>
                          <span className="text-blue-200">{u.username}</span>
                          {u.hunter_reputation > 0 && <span className="text-yellow-400 ml-1">({u.hunter_reputation} rep)</span>}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" className="h-6 px-2 text-xs bg-purple-600/30 hover:bg-purple-600/50 text-purple-200" onClick={() => handleInviteUser(u.id, "member")}>
                            Hunter
                          </Button>
                          <Button size="sm" className="h-6 px-2 text-xs bg-amber-600/30 hover:bg-amber-600/50 text-amber-200" onClick={() => handleInviteUser(u.id, "client")}>
                            Client
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Expedition Details */}
          <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #0d1535 0%, #080e25 100%)", border: "1px solid rgba(59,130,246,0.25)" }}>
            <h2 className="text-sm font-semibold text-blue-300 uppercase tracking-wider flex items-center gap-2">
              <Users className="w-4 h-4" /> Details
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-blue-400/60">Boss</span><span className="text-blue-100">{expedition.boss_name}</span></div>
              <div className="flex justify-between"><span className="text-blue-400/60">Time (Server/UTC)</span><span className="text-blue-200 flex items-center gap-1"><Clock className="w-3 h-3" />{expedition.expected_time ? new Date(expedition.expected_time).toLocaleString("en-GB", { timeZone: "UTC", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" }) : "TBD"}</span></div>
              {expedition.expected_time && <div className="flex justify-between"><span className="text-green-400/60 text-xs">Your Local Time</span><span className="text-green-300 text-xs">{new Date(expedition.expected_time).toLocaleString("en-GB", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" })}</span></div>}
              <div className="flex justify-between"><span className="text-blue-400/60">Buyer Price</span><span className="text-yellow-400">{formatMesos(expedition.price_for_client) || "Free"}</span></div>
              <div className="flex justify-between"><span className="text-blue-400/60">Slots</span><span className="text-blue-200">{totalFilled}/{expedition.total_slots || "?"}</span></div>
              {expedition.hb_provided && (
                <div className="flex items-center gap-2">
                  <img src="https://legends.ml/static/images/lib/skill/1301007.png" alt="HB" className="w-5 h-5" />
                  <span className="text-blue-200 text-xs">Hyper Body Provided</span>
                </div>
              )}
              {expedition.hp_gear_provided && (
                <div className="flex items-center gap-2">
                  <img src="https://royals-library.netlify.app/images/skills/0001001.png" alt="HP Gear" className="w-5 h-5" />
                  <span className="text-orange-200 text-xs">HP Gear Provided{expedition.hp_gear_value ? ` (+${expedition.hp_gear_value} HP)` : ""}</span>
                </div>
              )}
              {expedition.safe_run_guaranteed && (
                <div className="flex items-center gap-2">
                  <img src="https://royals-library.netlify.app/images/skills/2321006.png" alt="Safe Run" className="w-5 h-5" />
                  <span className="text-emerald-200 text-xs">100% Safe Guaranteed Run</span>
                </div>
              )}
              {expedition.expected_runtime && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-400" />
                  <span className="text-blue-200 text-xs">Expected Runtime: ~{expedition.expected_runtime} min</span>
                </div>
              )}
              {expedition.selling_service === false && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-300 text-xs bg-gray-500/20 px-2 py-0.5 rounded">No Buyers — Hunters Only</span>
                </div>
              )}
              {expedition.apr_required && (
                <div className="flex items-center gap-2">
                  <img src="https://legends.ml/static/images/lib/item/05050000.png" alt="APR" className="w-5 h-5" />
                  <span className="text-purple-200 text-xs">APR Required{expedition.apr_amount ? ` (x${expedition.apr_amount})` : ""}</span>
                </div>
              )}
              {expedition.onyx_apple_required && (
                <div className="flex items-center gap-2">
                  <img src="https://royals-library.netlify.app/images/items/02022179.png" alt="Onyx Apple" className="w-5 h-5" />
                  <span className="text-purple-200 text-xs">Onyx Apple Required{expedition.onyx_apple_amount ? ` (x${expedition.onyx_apple_amount})` : ""}</span>
                </div>
              )}
              {expedition.message && (
                <div className="pt-2 border-t border-blue-900/30">
                  <p className="text-xs text-blue-400/60 mb-1">Message</p>
                  <p className="text-blue-200 text-sm italic">"{expedition.message}"</p>
                </div>
              )}
            </div>

            {/* Job Requirements */}
            {!expedition.free_for_all && expedition.job_requirements?.length > 0 && (
              <div className="pt-3 border-t border-blue-900/30">
                <p className="text-xs text-purple-300 uppercase tracking-wider mb-2">Looking For</p>
                <div className="space-y-1">
                  {expedition.job_requirements.map((req, i) => {
                    const jobData = availableJobs[req.job] || {};
                    const skills = jobData.skills || [];
                    return (
                      <div key={i} className="flex items-center gap-2 p-1.5 rounded bg-indigo-500/10 border border-indigo-500/20">
                        {skills.map((skill, si) => (
                          <img key={si} src={skill.icon} alt={skill.name} className="w-5 h-5" title={`${req.job} - ${skill.name}`} />
                        ))}
                        <span className="text-xs text-indigo-200 flex-1">{req.job}</span>
                        <Badge className="bg-blue-500/20 text-blue-200 text-xs">x{req.count}</Badge>
                        {req.min_level && <Badge className="bg-purple-500/20 text-purple-200 text-xs">Lv.{req.min_level}+</Badge>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {expedition.free_for_all && (
              <div className="pt-2 border-t border-blue-900/30">
                <Badge className="bg-purple-500/20 text-purple-200 border-purple-500/30">Free For All - Any Job</Badge>
              </div>
            )}
          </div>

          {/* Search for Buyer Section (when no buyer assigned, no pending client, leader, AND selling service is enabled) */}
          {isLeader && !expedition.client_id && !expedition.has_pending_client && expedition.status === "ACTIVE" && expedition.selling_service !== false && (
            <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #451a03 0%, #1c0a00 100%)", border: "1px solid rgba(217,119,6,0.4)" }}>
              <h2 className="text-sm font-semibold text-amber-300 uppercase tracking-wider flex items-center gap-2">
                <Search className="w-4 h-4" /> Find a Buyer
              </h2>
              <p className="text-xs text-amber-400/60">Search the buyer queue for people looking to buy this boss run.</p>
              <Button
                className="w-full"
                style={{ background: "linear-gradient(180deg, #d97706 0%, #b45309 100%)", border: "2px solid #f59e0b" }}
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API_URL}/api/expeditions/wishes?boss_id=${expedition.boss_id}`);
                    const wishes = res.data.wishes || [];
                    setBuyerQueue(wishes);
                    if (wishes.length === 0) {
                      toast.info("No buyers in queue for this boss");
                    }
                  } catch (err) {
                    toast.error("Failed to search buyer queue");
                  }
                }}
                data-testid="search-buyer-btn"
              >
                <Search className="w-4 h-4 mr-2" /> Search Buyer Queue
              </Button>
              
              {/* Buyer Queue Results */}
              {buyerQueue.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-amber-500/20">
                  <p className="text-xs text-amber-300 font-semibold">{buyerQueue.length} Buyer(s) Found:</p>
                  {buyerQueue.map((wish) => (
                    <div key={wish.id} className="flex items-center gap-2 p-2 rounded bg-amber-900/20 border border-amber-500/20">
                      {wish.user_avatar ? (
                        <img src={wish.user_avatar} alt="" className="w-7 h-7 rounded-full border border-amber-500/40" />
                      ) : (
                        <div className="w-7 h-7 rounded-full border border-amber-500/40 bg-amber-900/30 flex items-center justify-center text-amber-400 font-bold text-xs">
                          {wish.username?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-amber-100 text-xs font-medium truncate cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); setProfileUserId(wish.user_id); }}>{wish.username}</p>
                        {wish.contact_ign && <p className="text-[10px] text-amber-400/60">IGN: {wish.contact_ign}</p>}
                        {wish.reward && <p className="text-[10px] text-yellow-400">Reward: {formatMesos(wish.reward)}</p>}
                        {/* Buyer requirement badges with mismatch indicators */}
                        {(wish.hb_required || wish.hp_gear_required || wish.safe_run_required) && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {wish.hb_required && (
                              <span className={`text-[10px] px-1 rounded ${expedition.hb_provided ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}`}>
                                {expedition.hb_provided ? "\u2713" : "\u2717"} Needs HB
                              </span>
                            )}
                            {wish.hp_gear_required && (
                              <span className={`text-[10px] px-1 rounded ${expedition.hp_gear_provided ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}`}>
                                {expedition.hp_gear_provided ? "\u2713" : "\u2717"} Needs HP Gear
                              </span>
                            )}
                            {wish.safe_run_required && (
                              <span className={`text-[10px] px-1 rounded ${expedition.safe_run_guaranteed ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}`}>
                                {expedition.safe_run_guaranteed ? "\u2713" : "\u2717"} Needs Safe Run
                              </span>
                            )}
                          </div>
                        )}
                        {/* Mismatch warning text */}
                        {(() => {
                          const mm = [];
                          if (wish.hb_required && !expedition.hb_provided) mm.push("Hyper Body");
                          if (wish.hp_gear_required && !expedition.hp_gear_provided) mm.push("HP Gear");
                          if (wish.safe_run_required && !expedition.safe_run_guaranteed) mm.push("Safe Run");
                          return mm.length > 0 ? <p className="text-[10px] text-red-400 mt-0.5">Missing: {mm.join(", ")}</p> : null;
                        })()}
                      </div>
                      <Button
                        size="sm"
                        className="h-7 px-2 bg-amber-600/30 hover:bg-amber-600/50 text-amber-200 border border-amber-500/40 text-[10px]"
                        onClick={async () => {
                          // Check for requirement mismatches before inviting
                          const mismatches = [];
                          if (wish.hb_required && !expedition.hb_provided) mismatches.push("Hyper Body");
                          if (wish.hp_gear_required && !expedition.hp_gear_provided) mismatches.push("HP Gear");
                          if (wish.safe_run_required && !expedition.safe_run_guaranteed) mismatches.push("Safe Run");
                          if (mismatches.length > 0) {
                            if (!window.confirm(`Buyer needs: ${mismatches.join(", ")} — but you do not offer ${mismatches.join(", ")}. Do you want to proceed?`)) return;
                          }
                          try {
                            await axios.post(`${API_URL}/api/expeditions/${id}/invite`, 
                              { user_id: wish.user_id, role: "client" },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            toast.success(`Invited ${wish.username} as buyer!`);
                            setBuyerQueue(prev => prev.filter(w => w.id !== wish.id));
                            fetchExpedition();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to invite");
                          }
                        }}
                        data-testid={`invite-buyer-${wish.id}`}
                      >
                        <UserPlus className="w-3 h-3 mr-1" /> Invite
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Buyer Info Section */}
          {expedition.client_id && (
            <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #451a03 0%, #1c0a00 100%)", border: "1px solid rgba(217,119,6,0.4)" }}>
              <h2 className="text-sm font-semibold text-amber-300 uppercase tracking-wider flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Buyer
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-amber-400/60">Username</span><span className="text-amber-100 cursor-pointer hover:underline" onClick={() => setProfileUserId(expedition.client_id)}>{expedition.client_username}</span></div>
                {expedition.client_contact_ign && isLeader && (
                  <div className="flex justify-between"><span className="text-amber-400/60">Contact IGN</span><span className="text-amber-200">{expedition.client_contact_ign}</span></div>
                )}
                <div className="flex justify-between">
                  <span className="text-amber-400/60">Status</span>
                  <Badge className={expedition.client_confirmed ? "bg-green-500/20 text-green-300" : "bg-yellow-500/20 text-yellow-300"}>
                    {expedition.client_confirmed ? "Confirmed" : "Pending Confirmation"}
                  </Badge>
                </div>
                {!expedition.client_confirmed && expedition.client_confirm_deadline && (
                  <p className="text-xs text-amber-400/60">
                    Deadline: {new Date(expedition.client_confirm_deadline).toLocaleString()}
                  </p>
                )}
              </div>
              {/* Client confirm/decline buttons */}
              {user && expedition.client_id === user.id && !expedition.client_confirmed && (
                <div className="space-y-2">
                  <Button
                    className="w-full font-bold"
                    style={{ background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)", border: "2px solid #22c55e" }}
                    onClick={async () => {
                      try {
                        await axios.put(`${API_URL}/api/expeditions/${id}/client-confirm`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success("Confirmed! You are the client.");
                        fetchExpedition();
                      } catch (err) { toast.error(err.response?.data?.detail || "Failed to confirm"); }
                    }}
                    data-testid="client-confirm-btn"
                  >
                    <Check className="w-4 h-4 mr-2" /> Confirm Participation
                  </Button>
                  <Button
                    className="w-full text-red-300 bg-red-600/20 hover:bg-red-600/40 border border-red-500/30"
                    onClick={async () => {
                      if (!window.confirm("Are you sure you want to decline this expedition?")) return;
                      try {
                        await axios.put(`${API_URL}/api/expeditions/${id}/client-decline`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success("Declined — you have been removed.");
                        fetchExpedition();
                      } catch (err) { toast.error(err.response?.data?.detail || "Failed to decline"); }
                    }}
                    data-testid="client-decline-btn"
                  >
                    <X className="w-4 h-4 mr-2" /> Decline
                  </Button>
                </div>
              )}
              {/* Leader: Mark Buyer as Dead (ONGOING only) */}
              {isLeader && expedition.status === "ONGOING" && !expedition.buyer_dead && (
                <Button
                  size="sm"
                  className="w-full bg-red-900/30 hover:bg-red-900/50 text-red-200 border border-red-700/40 text-xs"
                  onClick={async () => {
                    if (!window.confirm("Mark the buyer as dead? This will skip buyer confirmation when completing the expedition.")) return;
                    try {
                      await axios.put(`${API_URL}/api/expeditions/${id}/mark-buyer-dead`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success("Buyer marked as dead");
                      fetchExpedition();
                    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                  }}
                  data-testid="mark-buyer-dead-btn"
                >
                  <Skull className="w-3 h-3 mr-1" /> Mark Buyer as Dead
                </Button>
              )}
              {/* Show dead badge */}
              {expedition.buyer_dead && (
                <Badge className="bg-red-900/30 text-red-300 border border-red-700/40 w-full justify-center py-1">
                  <Skull className="w-3 h-3 mr-1" /> Buyer Marked as Dead
                </Badge>
              )}
              {/* Leader: Expel client (only before ONGOING) */}
              {isLeader && expedition.status === "ACTIVE" && (
                <Button
                  size="sm"
                  className="w-full bg-red-600/20 hover:bg-red-600/40 text-red-300 border border-red-500/30 text-xs"
                  onClick={async () => {
                    if (!window.confirm("Expel client from expedition?")) return;
                    try {
                      await axios.put(`${API_URL}/api/expeditions/${id}/expel-client`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success("Client expelled");
                      fetchExpedition();
                    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                  }}
                  data-testid="expel-client-btn"
                >
                  <X className="w-3 h-3 mr-1" /> Expel Client
                </Button>
              )}
            </div>
          )}

          {/* Available LFG Hunters (Leader only) */}
          {isLeader && expedition.status === "ACTIVE" && lfgHunters.filter(lfg => (lfg.boss_ids || []).includes(expedition.boss_id) || lfg.boss_id === expedition.boss_id).length > 0 && (
            <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #1a0d40 0%, #0a0628 100%)", border: "1px solid rgba(124,77,255,0.3)" }}>
              <h2 className="text-sm font-semibold text-purple-300 uppercase tracking-wider flex items-center gap-2">
                <Users className="w-4 h-4" /> Available Hunters (LFG)
              </h2>
              <div className="space-y-2">
                {lfgHunters
                  .filter(lfg => (lfg.boss_ids || []).includes(expedition.boss_id) || lfg.boss_id === expedition.boss_id)
                  .map((lfg) => (
                    <div key={lfg.id} className="flex items-center justify-between p-2 bg-black/30 rounded-lg text-sm">
                      <div>
                        <span className="text-purple-200 font-medium cursor-pointer hover:text-purple-100 hover:underline" onClick={(e) => { e.stopPropagation(); setProfileUserId(lfg.user_id); }}>{lfg.username}</span>
                        {lfg.hunter_reputation > 0 && <span className="text-yellow-400 ml-1 text-xs">({lfg.hunter_reputation} rep)</span>}
                        {lfg.has_hb && <Badge variant="outline" className="ml-1 text-xs py-0">HB</Badge>}
                        <div className="flex items-center gap-1 mt-0.5">
                          {(lfg.characters || []).map((char, ci) => {
                            const charSkills = char.skills || availableJobs[char.job]?.skills || [];
                            return (
                              <span key={ci} className="flex items-center gap-0.5 text-xs text-purple-300/70">
                                {charSkills.slice(0, 1).map((s, si) => <img key={si} src={s.icon} alt={s.name} className="w-4 h-4" />)}
                                Lv.{char.level}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <Button size="sm" className="h-7 px-2 text-xs bg-purple-600/30 hover:bg-purple-600/50 text-purple-200"
                        onClick={() => handleInviteUser(lfg.user_id, "member")}
                      >
                        Invite
                      </Button>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Members List */}
          <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #0d1535 0%, #080e25 100%)", border: "1px solid rgba(59,130,246,0.25)" }}>
            <h2 className="text-sm font-semibold text-blue-300 uppercase tracking-wider flex items-center gap-2">
              <Users className="w-4 h-4" /> Party ({totalFilled}/{expedition.total_slots})
            </h2>

            {/* Leader */}
            <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: "rgba(124,77,255,0.15)", border: "1px solid rgba(124,77,255,0.3)" }}>
              <Avatar className="w-10 h-10">
                <AvatarImage src={expedition.leader_avatar} />
                <AvatarFallback className="bg-purple-900 text-purple-200 text-sm">{expedition.leader_username?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-purple-100 font-medium cursor-pointer hover:text-purple-50 hover:underline" onClick={() => setProfileUserId(expedition.leader_id)}>{expedition.leader_username}</p>
                  <Badge className="bg-purple-500/30 text-purple-200 text-xs">Leader</Badge>
                </div>
                {expedition.leader_characters?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    {expedition.leader_characters.map((char, ci) => {
                      const charSkills = availableJobs[char.job]?.skills || [];
                      return (
                        <div key={ci} className="flex items-center gap-1 bg-purple-900/30 rounded px-1.5 py-0.5">
                          {charSkills.slice(0, 1).map((s, si) => (
                            <img key={si} src={s.icon} alt={s.name} className="w-4 h-4" />
                          ))}
                          <span className="text-xs text-purple-200 font-medium">{char.name || char.job}</span>
                          <span className="text-xs text-purple-300/50">({char.job})</span>
                          <span className="text-xs text-purple-300/70">Lv.{char.level}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Star className="w-3.5 h-3.5 text-yellow-400" />
                <span className="text-sm text-yellow-300">{expedition.leader_rep || 0}</span>
              </div>
            </div>

            {/* Client (Buyer) */}
            {expedition.client_username && (() => {
              const clientMember = allAccepted.find(m => m.user_id === expedition.client_id);
              const clientChars = expedition.client_characters || clientMember?.characters || clientMember?.applied_characters || [];
              const contactIGN = expedition.client_contact_ign || clientMember?.contact_ign || "";
              return (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={expedition.client_avatar} />
                    <AvatarFallback className="bg-amber-900 text-amber-200 text-sm">{expedition.client_username?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-amber-100 font-medium cursor-pointer hover:text-amber-50 hover:underline" onClick={() => setProfileUserId(expedition.client_id)}>{expedition.client_username}</p>
                      <Badge className="bg-amber-600/30 text-amber-300 border border-amber-500/40 text-[10px] px-1.5 py-0">Buyer</Badge>
                    </div>
                    {clientChars.length > 0 ? (
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {clientChars.map((char, ci) => {
                          const charSkills = char.skills || availableJobs[char.job]?.skills || [];
                          return (
                            <div key={ci} className="flex items-center gap-1 bg-amber-900/30 rounded px-1.5 py-0.5">
                              {charSkills.slice(0, 1).map((s, si) => (
                                <img key={si} src={s.icon} alt={s.name} className="w-4 h-4" />
                              ))}
                              <span className="text-xs text-amber-200 font-medium">{char.name || char.job}</span>
                              <span className="text-xs text-amber-300/50">({char.job})</span>
                              <span className="text-xs text-amber-300/60">Lv.{char.level}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : contactIGN ? (
                      <p className="text-xs text-amber-400/70 mt-0.5">IGN: {contactIGN}</p>
                    ) : (
                      <p className="text-xs text-amber-400/50 mt-0.5">Client (Buyer)</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-yellow-400" />
                      <span className="text-sm text-yellow-300">{expedition.client_rep || 0}</span>
                    </div>
                    {expedition.client_confirmed && <Check className="w-4 h-4 text-green-400" />}
                  </div>
                </div>
              );
            })()}

            {/* Accepted Members */}
            {accepted.map((member) => {
              const chars = member.characters || member.applied_characters || [];
              return (
                <div key={member.id || member.user_id} className="flex items-center gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={member.avatar} />
                    <AvatarFallback className="bg-blue-900 text-blue-200 text-sm">{member.username?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-blue-100 font-medium cursor-pointer hover:text-blue-50 hover:underline" onClick={() => setProfileUserId(member.user_id)}>{member.username}</p>
                    {chars.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {chars.map((char, ci) => {
                          const charSkills = char.skills || availableJobs[char.job]?.skills || [];
                          return (
                            <div key={ci} className="flex items-center gap-1 bg-blue-900/30 rounded px-1.5 py-0.5">
                              {charSkills.slice(0, 1).map((s, si) => (
                                <img key={si} src={s.icon} alt={s.name} className="w-4 h-4" />
                              ))}
                              <span className="text-xs text-blue-200 font-medium">{char.name || char.job}</span>
                              <span className="text-xs text-blue-300/50">({char.job})</span>
                              <span className="text-xs text-blue-300/60">Lv.{char.level}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <Badge className="bg-green-500/20 text-green-300 text-xs">Accepted</Badge>
                </div>
              );
            })}

            {/* Pending Members (only visible to leader) */}
            {isLeader && pending.length > 0 && (
              <>
                <div className="pt-2 border-t border-blue-900/30">
                  <p className="text-xs text-orange-300 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <UserPlus className="w-3 h-3" /> Pending Applications ({pending.length})
                  </p>
                </div>
                {pending.map((member) => {
                  const chars = member.characters || member.applied_characters || [];
                  const isBuyer = member.role === "client" || member.role === "buyer";
                  const buyerMismatches = isBuyer ? (() => {
                    const mm = [];
                    if (member.hb_required && !expedition.hb_provided) mm.push("Hyper Body");
                    if (member.hp_gear_required && !expedition.hp_gear_provided) mm.push("HP Gear");
                    if (member.safe_run_required && !expedition.safe_run_guaranteed) mm.push("Safe Run");
                    return mm;
                  })() : [];
                  return (
                    <div key={member.id || member.user_id} className="flex items-center gap-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20" data-testid={`pending-member-${member.user_id}`}>
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={member.avatar} />
                        <AvatarFallback className="bg-orange-900 text-orange-200 text-sm">{member.username?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-orange-100 font-medium cursor-pointer hover:text-orange-50 hover:underline" onClick={() => setProfileUserId(member.user_id)}>{member.username}</p>
                          {isBuyer && (
                            <Badge className="bg-amber-500/20 text-amber-300 text-[9px] px-1 py-0">Buyer</Badge>
                          )}
                          {member.contact_ign && (
                            <span className="text-xs text-orange-300/60">IGN: {member.contact_ign}</span>
                          )}
                        </div>
                        {member.message && (
                          <p className="text-xs text-orange-200/70 italic mt-0.5">"{member.message}"</p>
                        )}
                        {/* Buyer requirement badges with mismatch indicators */}
                        {isBuyer && (member.hb_required || member.hp_gear_required || member.safe_run_required) && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {member.hb_required && (
                              <span className={`text-[10px] px-1 rounded ${expedition.hb_provided ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}`}>
                                {expedition.hb_provided ? "\u2713" : "\u2717"} Needs HB
                              </span>
                            )}
                            {member.hp_gear_required && (
                              <span className={`text-[10px] px-1 rounded ${expedition.hp_gear_provided ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}`}>
                                {expedition.hp_gear_provided ? "\u2713" : "\u2717"} Needs HP Gear
                              </span>
                            )}
                            {member.safe_run_required && (
                              <span className={`text-[10px] px-1 rounded ${expedition.safe_run_guaranteed ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}`}>
                                {expedition.safe_run_guaranteed ? "\u2713" : "\u2717"} Needs Safe Run
                              </span>
                            )}
                          </div>
                        )}
                        {isBuyer && buyerMismatches.length > 0 && (
                          <p className="text-[10px] text-red-400 mt-0.5">Missing: {buyerMismatches.join(", ")}</p>
                        )}
                        {chars.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            {chars.map((char, ci) => {
                              const charSkills = availableJobs[char.job]?.skills || [];
                              return (
                                <div key={ci} className="flex items-center gap-1 bg-orange-900/30 rounded px-1.5 py-0.5">
                                  {charSkills.slice(0, 1).map((s, si) => (
                                    <img key={si} src={s.icon} alt={s.name} className="w-4 h-4" />
                                  ))}
                                  <span className="text-xs text-orange-200 font-medium">{char.name || char.job}</span>
                                  <span className="text-xs text-orange-300/50">({char.job})</span>
                                  <span className="text-xs text-orange-300/70">Lv.{char.level}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" className="h-7 w-7 bg-green-600/30 hover:bg-green-600/50 border border-green-500/40" onClick={() => {
                          if (isBuyer && buyerMismatches.length > 0) {
                            if (!window.confirm(`Buyer needs: ${buyerMismatches.join(", ")} — but you do not offer ${buyerMismatches.join(", ")}. Do you want to proceed?`)) return;
                          }
                          handleAcceptMember(member.id);
                        }} data-testid={`accept-member-${member.user_id}`}>
                          <Check className="w-3 h-3 text-green-300" />
                        </Button>
                        <Button size="icon" className="h-7 w-7 bg-red-600/30 hover:bg-red-600/50 border border-red-500/40" onClick={() => handleRejectMember(member.id)} data-testid={`reject-member-${member.user_id}`}>
                          <X className="w-3 h-3 text-red-300" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Non-leader: Pending count */}
            {!isLeader && pending.length > 0 && (
              <div className="text-xs text-blue-400/50 text-center">
                {pending.length} application(s) pending review
              </div>
            )}
          </div>
        </div>

        {/* Right: Chat */}
        <div className="lg:col-span-2">
          {/* Disclaimer */}
          <div className="mb-2 px-3 py-2 rounded-lg flex items-start gap-2" style={{ background: "rgba(234,179,8,0.1)", border: "1px solid rgba(234,179,8,0.3)" }}>
            <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-yellow-300/80">All trade agreements must be finalized and confirmed in-game. Hunter's Chance is not responsible for any disputes.</p>
            <div className="flex gap-1 ml-auto flex-shrink-0">
              <Button size="sm" variant="ghost" className="text-[#FF5E5B] hover:bg-[#FF5E5B]/10 h-6 px-2" onClick={() => setShowKofiModal(true)}>
                <Heart className="w-3 h-3 mr-1 fill-[#FF5E5B]" /> Support
              </Button>
              {user && (
                <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-6 px-2" onClick={() => setShowReportModal(true)} data-testid="report-expedition-btn">
                  <Flag className="w-3 h-3 mr-1" /> Report
                </Button>
              )}
            </div>
          </div>

          {/* Leave Expedition button for non-leaders */}
          {user && !isLeader && expedition.is_participant && ["ACTIVE", "FILLING"].includes(expedition.status) && (
            <div className="mb-2">
              <Button
                size="sm"
                className="w-full bg-red-600/20 hover:bg-red-600/40 text-red-300 border border-red-500/30 text-xs"
                onClick={async () => {
                  if (!window.confirm("Leave this expedition?")) return;
                  try {
                    await axios.delete(`${API_URL}/api/expeditions/${id}/leave`, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success("Left expedition");
                    fetchExpedition();
                  } catch (err) { toast.error(err.response?.data?.detail || "Failed to leave"); }
                }}
                data-testid="leave-expedition-btn"
              >
                <X className="w-3 h-3 mr-1" /> Leave Expedition
              </Button>
            </div>
          )}
          <div className="rounded-xl overflow-hidden flex flex-col" style={{ background: "linear-gradient(180deg, #0d1535 0%, #080e25 100%)", border: "1px solid rgba(59,130,246,0.25)", height: "calc(100vh - 220px)" }}>
            {/* Chat Header */}
            <div className="px-4 py-3 border-b border-blue-900/30 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-blue-400" />
              <span className="text-blue-200 text-sm font-semibold">Expedition Chat</span>
              <span className="text-xs text-blue-400/40 ml-auto">{messages.length} messages</span>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 px-4 py-3">
              <div className="space-y-3">
                {messages.length === 0 && (
                  <div className="text-center py-12 text-blue-400/40">
                    <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No messages yet. Coordinate with your party!</p>
                  </div>
                )}
                {messages.map((msg) => {
                  const isMe = user && msg.user_id === user.id;
                  return (
                    <div key={msg.id} className={`flex gap-2 ${isMe ? "flex-row-reverse" : ""}`}>
                      <Avatar className="w-7 h-7 flex-shrink-0">
                        <AvatarImage src={msg.avatar} />
                        <AvatarFallback className="bg-blue-900/50 text-blue-200 text-xs">{msg.username?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className={`max-w-[70%] ${isMe ? "items-end" : "items-start"}`}>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-xs font-medium cursor-pointer hover:underline ${isMe ? "text-blue-300" : "text-blue-400/70"}`} onClick={() => setProfileUserId(msg.user_id)}>{msg.username}</span>
                          <span className="text-xs text-blue-400/30">{formatTime(msg.created_at)}</span>
                        </div>
                        <div className={`rounded-xl px-3 py-2 text-sm ${isMe ? "bg-blue-600/30 text-blue-100" : "bg-black/40 text-blue-200"}`}>
                          {msg.message}
                          {msg.screenshot_url && (
                            <img src={msg.screenshot_url.startsWith("http") ? msg.screenshot_url : `${API_URL}${msg.screenshot_url}`} alt="Screenshot" className="mt-2 rounded-lg max-w-full max-h-48 cursor-pointer" onClick={() => window.open(msg.screenshot_url.startsWith("http") ? msg.screenshot_url : `${API_URL}${msg.screenshot_url}`, "_blank")} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={chatEndRef} />
              </div>
            </ScrollArea>

            {/* Chat Input */}
            {expedition.is_participant && (
              <div className="px-4 py-3 border-t border-blue-900/30">
                <div className="flex items-center gap-2">
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleUploadScreenshot} />
                  <Button variant="ghost" size="icon" className="text-blue-400 hover:bg-blue-500/10 h-9 w-9" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="expedition-chat-upload">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                  </Button>
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                    onPaste={async (e) => {
                      const items = e.clipboardData?.items;
                      if (!items) return;
                      for (const item of items) {
                        if (item.type.startsWith('image/')) {
                          e.preventDefault();
                          const file = item.getAsFile();
                          if (file) {
                            const fakeEvent = { target: { files: [file] } };
                            handleUploadScreenshot(fakeEvent);
                          }
                          break;
                        }
                      }
                    }}
                    placeholder="Type a message... (Ctrl+V to paste image)"
                    className="bg-black/40 border-blue-900/40 text-blue-100 placeholder:text-blue-400/30 h-9"
                    data-testid="expedition-chat-input"
                  />
                  <Button size="icon" className="h-9 w-9" style={{ background: "linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%)" }} onClick={() => handleSendMessage()} disabled={sendingMessage || !newMessage.trim()} data-testid="expedition-chat-send">
                    {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}

            {!expedition.is_participant && (
              <div className="px-4 py-3 border-t border-blue-900/30 text-center text-blue-400/40 text-sm">
                Only participants can chat
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Buyer Confirm Success Section */}
      {user && expedition.status === "AWAITING_BUYER_CONFIRM" && expedition.client_id === user.id && !expedition.buyer_confirmed_success && (
        <div className="max-w-6xl mx-auto px-4 pb-6">
          <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #0a2010 0%, #041208 100%)", border: "2px solid rgba(34,197,94,0.5)" }}>
            <h2 className="text-sm font-semibold text-green-300 uppercase tracking-wider flex items-center gap-2">
              <Trophy className="w-4 h-4" /> Confirm Expedition Success
            </h2>
            <p className="text-green-200/70 text-sm">The leader has marked this expedition as successful. Please confirm.</p>
            <div className="flex gap-2">
              <Button
                className="flex-1 font-bold"
                style={{ background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)", border: "2px solid #22c55e" }}
                onClick={handleBuyerConfirmSuccess}
                data-testid="buyer-confirm-success-btn"
              >
                <Check className="w-4 h-4 mr-2" /> Confirm Success
              </Button>
              <Button
                className="flex-1 text-red-300 bg-red-600/20 hover:bg-red-600/40 border border-red-500/30"
                onClick={async () => {
                  if (!window.confirm("Dispute this expedition's success? It will be reverted to ONGOING.")) return;
                  try {
                    await axios.put(`${API_URL}/api/expeditions/${id}/buyer-decline-success`, {}, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success("Disputed — expedition reverted to ONGOING.");
                    fetchExpedition();
                  } catch (err) { toast.error(err.response?.data?.detail || "Failed to dispute"); }
                }}
                data-testid="buyer-decline-success-btn"
              >
                <X className="w-4 h-4 mr-2" /> Dispute
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Buyer already confirmed - show PAID badge */}
      {user && expedition.status === "AWAITING_BUYER_CONFIRM" && expedition.client_id === user.id && expedition.buyer_confirmed_success && (
        <div className="max-w-6xl mx-auto px-4 pb-6">
          <div className="rounded-xl p-4" style={{ background: "linear-gradient(180deg, #0a2010 0%, #041208 100%)", border: "2px solid rgba(34,197,94,0.5)" }}>
            <Button
              className="w-full font-bold cursor-default opacity-90"
              style={{ background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)", border: "2px solid #22c55e" }}
              disabled
              data-testid="buyer-paid-btn"
            >
              <Check className="w-4 h-4 mr-2" /> Paid
            </Button>
          </div>
        </div>
      )}

      {/* Splits Management Section - Editable for leader during active split states */}
      {isLeader && (expedition.status === "SETTLING_SPLITS" || (expedition.status === "AWAITING_BUYER_CONFIRM" && expedition.keep_for_splits)) && (
        <div className="max-w-6xl mx-auto px-4 pb-6">
          <div className="rounded-xl p-4 space-y-4" style={{ background: "linear-gradient(180deg, #1a1040 0%, #0f0828 100%)", border: "2px solid rgba(124,77,255,0.5)" }}>
            <h2 className="text-sm font-semibold text-purple-300 uppercase tracking-wider flex items-center gap-2">
              <Coins className="w-4 h-4" /> Splits Management
            </h2>

            {/* Add Drop */}
            <div className="p-3 rounded-lg bg-black/30 border border-purple-500/20 space-y-2">
              <Label className="text-purple-200 text-xs">Add Drop</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Item name (e.g. GEN 30, MW 20)"
                  value={newDropName}
                  onChange={(e) => setNewDropName(e.target.value)}
                  className="flex-1 bg-black/40 border-purple-500/40 text-white text-xs h-8"
                  data-testid="drop-name-input"
                />
                <Input
                  placeholder="Value (M)"
                  type="number"
                  value={newDropValue}
                  onChange={(e) => setNewDropValue(e.target.value)}
                  className="w-24 bg-black/40 border-purple-500/40 text-white text-xs h-8"
                  data-testid="drop-value-input"
                />
                <Button
                  size="sm"
                  className="bg-purple-600/30 hover:bg-purple-600/50 text-purple-200 border border-purple-500/40 h-8 px-3"
                  onClick={handleAddDrop}
                  disabled={addingDrop || !newDropName.trim() || !newDropValue}
                  data-testid="add-drop-btn"
                >
                  {addingDrop ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                </Button>
              </div>
            </div>

            {/* Drops List */}
            {expedition.drops && expedition.drops.length > 0 && (
              <div className="space-y-2">
                <Label className="text-purple-200 text-xs">Drops</Label>
                {expedition.drops.map((drop) => (
                  <div key={drop.id} className="flex items-center gap-2 p-2 rounded bg-black/30 border border-purple-500/20">
                    <Package className={`w-4 h-4 ${drop.sold ? "text-green-400" : "text-purple-400"}`} />
                    <span className={`flex-1 text-sm ${drop.sold ? "text-green-300 line-through" : "text-purple-100"}`}>{drop.name}</span>
                    <Input
                      type="number"
                      value={drop.value}
                      onChange={(e) => handleUpdateDrop(drop.id, { value: parseFloat(e.target.value) || 0 })}
                      className="w-20 bg-black/40 border-purple-500/40 text-white text-xs h-7"
                    />
                    <span className="text-xs text-purple-300">M</span>
                    <Button
                      size="sm"
                      variant={drop.sold ? "default" : "outline"}
                      className={`h-7 px-2 text-xs ${drop.sold ? "bg-green-600/30 text-green-200" : "text-purple-200 border-purple-500/40"}`}
                      onClick={() => handleUpdateDrop(drop.id, { sold: !drop.sold })}
                    >
                      {drop.sold ? "Sold" : "Mark Sold"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleDeleteDrop(drop.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <div className="text-right space-y-1 text-sm">
                  <div className="text-purple-300">
                    Gross Total: <span className="font-bold text-purple-100">{formatMesos(expedition.total_drop_value || 0)}</span>
                  </div>
                  <div className="text-red-300/80">
                    Tax (3%): <span className="font-bold text-red-300">-{formatMesos(expedition.tax_amount || (expedition.total_drop_value || 0) * 0.03)}</span>
                  </div>
                  <div className="text-green-300 border-t border-purple-500/30 pt-1">
                    Net Total: <span className="font-bold text-green-200">{formatMesos(expedition.net_value || (expedition.total_drop_value || 0) * 0.97)}</span>
                  </div>
                  {expedition.hunter_payments?.length > 0 && (
                    <div className="text-blue-300">
                      Per Hunter ({expedition.hunter_payments.length}): <span className="font-bold text-blue-200">{formatMesos(expedition.hunter_payments[0]?.share || 0)}</span>
                    </div>
                  )}
                </div>
                
                {/* Per-item breakdown per hunter */}
                {expedition.hunter_payments?.length > 0 && expedition.drops?.length > 1 && (
                  <div className="mt-3 rounded bg-black/20 border border-purple-500/15 p-2">
                    <p className="text-[10px] text-purple-400/60 uppercase tracking-wider mb-1">Per Item Split (after 3% tax)</p>
                    <div className="space-y-1">
                      {expedition.drops.map((drop) => {
                        const dropNet = drop.value * 0.97;
                        const perHunter = dropNet / expedition.hunter_payments.length;
                        return (
                          <div key={drop.id} className="flex justify-between text-xs">
                            <span className="text-purple-300/80 truncate mr-2">{drop.name}</span>
                            <span className="text-blue-300 whitespace-nowrap">{formatMesos(Math.round(perHunter * 100) / 100)}/ea</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Hunter Payments */}
            {expedition.hunter_payments && expedition.hunter_payments.length > 0 && (
              <div className="space-y-2">
                <Label className="text-purple-200 text-xs">Hunter Shares ({expedition.hunter_payments.length} hunters)</Label>
                {expedition.hunter_payments.map((hp) => (
                  <div key={hp.user_id} className="flex items-center justify-between p-2 rounded bg-black/30 border border-purple-500/20">
                    <span className={`text-sm cursor-pointer hover:underline ${hp.paid ? "text-green-300" : "text-purple-100"}`} onClick={() => setProfileUserId(hp.user_id)}>{hp.username}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-purple-300">{formatMesos(hp.share || 0)}</span>
                      <Button
                        size="sm"
                        variant={hp.paid ? "default" : "outline"}
                        className={`h-7 px-2 text-xs ${hp.paid ? "bg-green-600/30 text-green-200" : "text-purple-200 border-purple-500/40"}`}
                        onClick={() => handleMarkPaid(hp.user_id, !hp.paid)}
                      >
                        {hp.paid ? <><Check className="w-3 h-3 mr-1" /> Paid</> : "Mark Paid"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Settle Button */}
            <div className="pt-3 border-t border-purple-500/20">
              <Button
                className="w-full font-bold"
                style={{ background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)", border: "2px solid #22c55e" }}
                onClick={handleSettle}
                disabled={
                  expedition.drops?.some(d => !d.sold) || 
                  expedition.hunter_payments?.some(hp => !hp.paid)
                }
                data-testid="settle-expedition-btn"
              >
                <Trophy className="w-4 h-4 mr-2" /> Settle & Archive
              </Button>
              {(expedition.drops?.some(d => !d.sold) || expedition.hunter_payments?.some(hp => !hp.paid)) && (
                <p className="text-xs text-purple-300/60 text-center mt-2">
                  All drops must be sold and all hunters must be paid before settling
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Read-only Splits Summary (visible to leader and hunters after settled/succeeded/archived) */}
      {(isLeader || expedition.is_member) && ["SUCCEEDED", "ARCHIVED"].includes(expedition.status) && expedition.drops?.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 pb-6">
          <div className="rounded-xl p-4 space-y-4" style={{ background: "linear-gradient(180deg, #1a1040 0%, #0f0828 100%)", border: "1px solid rgba(124,77,255,0.3)" }}>
            <h2 className="text-sm font-semibold text-purple-300 uppercase tracking-wider flex items-center gap-2">
              <Coins className="w-4 h-4" /> Splits Summary
            </h2>

            {/* Drops list (read-only) */}
            <div className="space-y-1">
              {expedition.drops.map((drop) => (
                <div key={drop.id} className="flex items-center justify-between p-2 rounded bg-black/20 border border-purple-500/10">
                  <span className="text-sm text-purple-100">{drop.name}</span>
                  <span className="text-sm text-purple-300 font-medium">{formatMesos(drop.value)}</span>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="text-right space-y-1 text-sm">
              <div className="text-purple-300">Gross Total: <span className="font-bold text-purple-100">{formatMesos(expedition.total_drop_value || 0)}</span></div>
              <div className="text-red-300/80">Tax (3%): <span className="font-bold text-red-300">-{formatMesos(expedition.tax_amount || (expedition.total_drop_value || 0) * 0.03)}</span></div>
              <div className="text-green-300 border-t border-purple-500/30 pt-1">Net Total: <span className="font-bold text-green-200">{formatMesos(expedition.net_value || (expedition.total_drop_value || 0) * 0.97)}</span></div>
              {expedition.hunter_payments?.length > 0 && (
                <div className="text-blue-300">Per Hunter ({expedition.hunter_payments.length}): <span className="font-bold text-blue-200">{formatMesos(expedition.hunter_payments[0]?.share || 0)}</span></div>
              )}
            </div>

            {/* Hunter payment status */}
            {expedition.hunter_payments?.length > 0 && (
              <div className="space-y-1">
                <Label className="text-purple-200 text-xs">Hunter Payments</Label>
                {expedition.hunter_payments.map((hp) => (
                  <div key={hp.user_id} className="flex items-center justify-between p-2 rounded bg-black/20 border border-purple-500/10">
                    <span className="text-sm text-purple-100 cursor-pointer hover:underline" onClick={() => setProfileUserId(hp.user_id)}>{hp.username}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-purple-300">{formatMesos(hp.share || 0)}</span>
                      <Badge className={hp.paid ? "bg-green-600/30 text-green-200" : "bg-red-600/30 text-red-200"}>{hp.paid ? "Paid" : "Unpaid"}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mark Success Modal */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent className="max-w-md" style={{ background: "linear-gradient(180deg, #1a1040 0%, #0f0828 100%)", border: "2px solid #7c4dff" }}>
          <DialogHeader>
            <DialogTitle className="text-purple-300 flex items-center gap-2">
              <Trophy className="w-5 h-5" /> Mark Expedition as Success
            </DialogTitle>
            <DialogDescription className="text-purple-200/60">
              Complete the {expedition.boss_name} expedition
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Buyer alive checkbox - only if expedition has a buyer who is not marked dead */}
            {expedition.client_id && !expedition.buyer_dead && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-black/30 border border-amber-500/30">
                <Checkbox
                  id="buyer_alive"
                  checked={completeForm.buyer_alive}
                  onCheckedChange={(checked) => setCompleteForm({ ...completeForm, buyer_alive: checked })}
                  className="mt-1 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                />
                <div>
                  <Label htmlFor="buyer_alive" className="text-amber-200 cursor-pointer">Buyer was alive/present</Label>
                  <p className="text-xs text-amber-300/60 mt-1">
                    {completeForm.buyer_alive 
                      ? "Buyer will need to confirm the success" 
                      : "Expedition will be marked as success directly"}
                  </p>
                </div>
              </div>
            )}

            {/* Keep for splits checkbox */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-black/30 border border-purple-500/30">
              <Checkbox
                id="keep_for_splits"
                checked={completeForm.keep_for_splits}
                onCheckedChange={(checked) => setCompleteForm({ ...completeForm, keep_for_splits: checked })}
                className="mt-1 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
              />
              <div>
                <Label htmlFor="keep_for_splits" className="text-purple-200 cursor-pointer">Keep up for Splits</Label>
                <p className="text-xs text-purple-300/60 mt-1">
                  {completeForm.keep_for_splits 
                    ? "You can declare drops and manage payouts to hunters" 
                    : "Expedition will go directly to archive"}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCompleteModal(false)}>Cancel</Button>
            <Button
              className="font-bold"
              style={{ background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)", border: "2px solid #22c55e" }}
              onClick={handleMarkSuccess}
              disabled={completing}
              data-testid="confirm-mark-success-btn"
            >
              {completing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Mark as Success
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Expedition Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#0f0828] border border-purple-500/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-blue-200">Edit Expedition</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-blue-200 text-sm">Expected Time</Label>
              <Input
                type="datetime-local"
                value={editForm.expected_time || ""}
                min={new Date().toISOString().slice(0, 16)}
                onChange={(e) => setEditForm({ ...editForm, expected_time: e.target.value })}
                className="bg-black/60 border-blue-400/60 text-white [color-scheme:dark] cursor-pointer"
                style={{ colorScheme: "dark" }}
                data-testid="edit-expedition-time"
              />
              <div className="mt-1 space-y-0.5 p-2 rounded bg-black/30 border border-blue-500/20">
                <p className="text-[10px] text-blue-300/90 font-semibold">
                  Server Time (UTC): <span className="text-blue-200 font-mono">{new Date().toLocaleString("en-GB", { timeZone: "UTC", hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short", timeZoneName: "short" })}</span>
                </p>
                <p className="text-[10px] text-green-300/90 font-semibold">
                  Your Local Time: <span className="text-green-200 font-mono">{new Date().toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short", timeZoneName: "short" })}</span>
                </p>
                {editForm.expected_time && (
                  <>
                    <p className="text-[10px] text-yellow-300/90">
                      Selected (Server/UTC): <span className="text-yellow-200 font-mono">{new Date(editForm.expected_time).toLocaleString("en-GB", { timeZone: "UTC", hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short", year: "numeric", timeZoneName: "short" })}</span>
                    </p>
                    <p className="text-[10px] text-green-300/70">
                      Selected (Your Time): <span className="text-green-200 font-mono">{new Date(editForm.expected_time).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short", year: "numeric", timeZoneName: "short" })}</span>
                    </p>
                  </>
                )}
                <p className="text-[10px] text-gray-400/60">The calendar input uses your local timezone. The server runs on UTC time.</p>
              </div>
            </div>
            <div>
              <Label className="text-blue-200 text-sm">Slots (incl. you)</Label>
              <Input
                type="number"
                min={1}
                max={30}
                value={editForm.total_slots || ""}
                onChange={(e) => setEditForm({ ...editForm, total_slots: parseInt(e.target.value) || 1 })}
                className="bg-black/40 border-blue-500/40 text-white"
                data-testid="edit-expedition-slots"
              />
            </div>
            <div>
              <Label className="text-blue-200 text-sm">Buyer Price (in millions)</Label>
              <Input
                type="text"
                value={editForm.price_for_client || ""}
                onChange={(e) => setEditForm({ ...editForm, price_for_client: e.target.value })}
                className="bg-black/40 border-blue-500/40 text-white"
                placeholder="e.g. 50"
                data-testid="edit-expedition-price"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editForm.hb_provided || false}
                onChange={(e) => setEditForm({ ...editForm, hb_provided: e.target.checked })}
                className="accent-blue-500"
                data-testid="edit-expedition-hb"
              />
              <img src="https://legends.ml/static/images/lib/skill/1301007.png" alt="HB" className="w-5 h-5" />
              <Label className="text-blue-200 text-sm">Hyper Body</Label>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.hp_gear_provided || false}
                  onChange={(e) => setEditForm({ ...editForm, hp_gear_provided: e.target.checked, hp_gear_value: e.target.checked ? editForm.hp_gear_value : "" })}
                  className="accent-orange-500"
                  data-testid="edit-expedition-hp-gear"
                />
                <img src="https://royals-library.netlify.app/images/skills/0001001.png" alt="HP Gear" className="w-5 h-5" />
                <Label className="text-orange-200 text-sm">HP Gear</Label>
              </div>
              {editForm.hp_gear_provided && (
                <Input
                  type="number" min="0" step="100" placeholder="Total HP provided (e.g. 2000)"
                  value={editForm.hp_gear_value || ""}
                  onChange={(e) => setEditForm({ ...editForm, hp_gear_value: e.target.value })}
                  className="bg-black/40 border-orange-500/40 text-white ml-7"
                  data-testid="edit-expedition-hp-gear-value"
                />
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editForm.safe_run_guaranteed || false}
                onChange={(e) => setEditForm({ ...editForm, safe_run_guaranteed: e.target.checked })}
                className="accent-emerald-500"
                data-testid="edit-expedition-safe-run"
              />
              <img src="https://royals-library.netlify.app/images/skills/2321006.png" alt="Safe" className="w-5 h-5" />
              <Label className="text-emerald-200 text-sm">100% Safe Guaranteed Run</Label>
            </div>
            {/* Selling Service */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editForm.selling_service || false}
                onChange={(e) => setEditForm({ ...editForm, selling_service: e.target.checked })}
                className="accent-yellow-500"
                data-testid="edit-expedition-selling"
              />
              <DollarSign className="w-4 h-4 text-yellow-400" />
              <Label className="text-yellow-200 text-sm">Selling Service (allow buyers)</Label>
            </div>
            {/* Expected Runtime */}
            <div>
              <Label className="text-blue-200 text-sm">Expected Runtime (min)</Label>
              <Input
                type="number"
                min="1"
                max="600"
                placeholder="e.g., 30"
                value={editForm.expected_runtime || ""}
                onChange={(e) => setEditForm({ ...editForm, expected_runtime: e.target.value })}
                className="bg-black/40 border-blue-500/40 text-white"
                data-testid="edit-expedition-runtime"
              />
            </div>
            {/* APR */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.apr_required || false}
                  onChange={(e) => setEditForm({ ...editForm, apr_required: e.target.checked, apr_amount: e.target.checked ? editForm.apr_amount : "" })}
                  className="accent-purple-500"
                  data-testid="edit-expedition-apr"
                />
                <img src="https://legends.ml/static/images/lib/item/05050000.png" alt="APR" className="w-5 h-5" />
                <Label className="text-purple-200 text-sm">APR Required</Label>
              </div>
              {editForm.apr_required && (
                <Input
                  type="number" min="1" placeholder="Amount"
                  value={editForm.apr_amount || ""}
                  onChange={(e) => setEditForm({ ...editForm, apr_amount: e.target.value })}
                  className="bg-black/40 border-purple-500/40 text-white ml-7 w-28"
                  data-testid="edit-expedition-apr-amount"
                />
              )}
            </div>
            {/* Onyx Apple */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.onyx_apple_required || false}
                  onChange={(e) => setEditForm({ ...editForm, onyx_apple_required: e.target.checked, onyx_apple_amount: e.target.checked ? editForm.onyx_apple_amount : "" })}
                  className="accent-purple-500"
                  data-testid="edit-expedition-onyx"
                />
                <img src="https://royals-library.netlify.app/images/items/02022179.png" alt="Onyx Apple" className="w-5 h-5" />
                <Label className="text-purple-200 text-sm">Onyx Apple Required</Label>
              </div>
              {editForm.onyx_apple_required && (
                <Input
                  type="number" min="1" placeholder="Amount"
                  value={editForm.onyx_apple_amount || ""}
                  onChange={(e) => setEditForm({ ...editForm, onyx_apple_amount: e.target.value })}
                  className="bg-black/40 border-purple-500/40 text-white ml-7 w-28"
                  data-testid="edit-expedition-onyx-amount"
                />
              )}
            </div>
            <div>
              <Label className="text-blue-200 text-sm">Message</Label>
              <textarea
                value={editForm.message || ""}
                onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                className="w-full bg-black/40 border border-blue-500/40 text-white rounded-md p-2 text-sm resize-none h-20"
                data-testid="edit-expedition-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleSaveEdit}
              disabled={saving}
              data-testid="save-edit-expedition-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400"><Flag className="w-4 h-4" /> Report Expedition</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Describe the issue..."
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            className="min-h-[100px]"
            data-testid="report-reason-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowReportModal(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleReport} disabled={reporting || !reportReason.trim()} data-testid="report-submit-btn">
              {reporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Flag className="w-4 h-4 mr-1" />} Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <KofiModal open={showKofiModal} onOpenChange={setShowKofiModal} />
      <UserProfileModal
        open={!!profileUserId}
        onOpenChange={(v) => { if (!v) setProfileUserId(null); }}
        userId={profileUserId}
      />
    </div>
  );
};

export default ExpeditionDetailPage;
